import { supabase } from '@/lib/supabase'
import type { EstadoCuenta, ImportRow, ParseResult, TipoServicio } from './types'

// -----------------------------------------------------------------------------
// Tipos del diff
// -----------------------------------------------------------------------------

/** Campos del asociado que consideramos "maestros del core" — el importer solo
 *  debe comparar estos y, mas adelante, solo tocar estos al aplicar. Deja
 *  fuera columnas NeoCore-only (notas, flags KYC, etc.) que agreguemos a futuro. */
export const ASOCIADO_CORE_FIELDS = [
  'dpi',
  'nombre_completo',
  'genero',
  'profesion',
  'direccion',
  'municipio',
  'departamento',
  'telefono_e164',
  'usuario_core',
  'actividad_economica',
] as const

export const CUENTA_CORE_FIELDS = [
  'tipo_servicio',
  'pin_core',
  'fecha_apertura',
  'ultimo_movimiento',
  'fecha_vencimiento',
  'fecha_cierre',
  'cierre_anticipado',
  'saldo_disponible',
  'saldo_pignorado',
  'saldo_total',
  'estado',
] as const

export type AsociadoDb = {
  id: string
  no_asociado: string
  dpi: string
  nombre_completo: string
  genero: 'MASCULINO' | 'FEMENINO'
  profesion: string | null
  direccion: string | null
  municipio: string | null
  departamento: string | null
  telefono_e164: string | null
  usuario_core: string
  actividad_economica: string | null
}

export type CuentaDb = {
  cuenta_id: string
  asociado_id: string
  no_asociado: string
  tipo_servicio: TipoServicio
  pin_core: string | null
  fecha_apertura: string
  ultimo_movimiento: string | null
  fecha_vencimiento: string | null
  fecha_cierre: string | null
  cierre_anticipado: boolean
  saldo_disponible: number
  saldo_pignorado: number
  saldo_total: number
  estado: EstadoCuenta
}

export type DiffStatus = 'nuevo' | 'modificado' | 'sin_cambios'

export type AsociadoDiff = {
  no_asociado: string
  nombre_completo: string
  dpi: string
  status: DiffStatus
  changedFields: string[] // solo si status = 'modificado'
  row: ImportRow
  dbRow: AsociadoDb | null
}

export type CuentaDiff = {
  cuenta_id: string
  no_asociado: string
  tipo_servicio: TipoServicio
  status: DiffStatus
  changedFields: string[]
  row: ImportRow
  dbRow: CuentaDb | null
}

export type HistoricRun = {
  id: string
  archivo_nombre: string
  archivo_hash: string
  status: 'pending' | 'running' | 'ok' | 'partial' | 'failed'
  rows_total: number | null
  started_at: string
  finished_at: string | null
}

export type DiffWarning = {
  severity: 'warn' | 'error'
  message: string
}

export type DiffResult = {
  asociados: AsociadoDiff[]
  cuentas: CuentaDiff[]
  warnings: DiffWarning[]
  previousRunSameHash: HistoricRun | null
  counts: {
    asociadosNuevos: number
    asociadosModificados: number
    asociadosSinCambios: number
    cuentasNuevas: number
    cuentasModificadas: number
    cuentasSinCambios: number
  }
}

// -----------------------------------------------------------------------------
// Helpers de comparacion
// -----------------------------------------------------------------------------

/** Normaliza strings para comparar: trim + nullish igual a null. */
function normStr(v: unknown): string | null {
  if (v === null || v === undefined) return null
  const s = String(v).trim()
  return s === '' ? null : s
}

/** Normaliza numeros con tolerancia de 0.01 para decimales. */
function numEq(a: number | string | null | undefined, b: number | string | null | undefined): boolean {
  const na = typeof a === 'string' ? parseFloat(a) : (a ?? 0)
  const nb = typeof b === 'string' ? parseFloat(b) : (b ?? 0)
  return Math.abs(na - nb) < 0.01
}

/**
 * Compara fechas por dia calendario Guatemala (UTC-6). Las fechas del core
 * (apertura, vencimiento, ultimo movimiento, cierre) son semanticamente
 * day-granular — un plazo fijo vence "un dia", no a una hora especifica.
 * Comparar solo por dia evita falsos positivos por desfases de timezone
 * entre el parser TS (SheetJS) y el seed Python (pandas + offset manual).
 */
function dateEq(a: string | null, b: string | null): boolean {
  if (a === null && b === null) return true
  if (a === null || b === null) return false
  const da = new Date(a)
  const db = new Date(b)
  if (isNaN(da.getTime()) || isNaN(db.getTime())) return false
  const dayGT = (d: Date) =>
    new Date(d.getTime() - 6 * 60 * 60 * 1000).toISOString().slice(0, 10)
  return dayGT(da) === dayGT(db)
}

function diffAsociadoFields(row: ImportRow, db: AsociadoDb): string[] {
  const changes: string[] = []
  if (normStr(row.dpi) !== normStr(db.dpi)) changes.push('dpi')
  if (normStr(row.nombre_completo) !== normStr(db.nombre_completo)) changes.push('nombre_completo')
  if (row.genero !== db.genero) changes.push('genero')
  if (normStr(row.profesion) !== normStr(db.profesion)) changes.push('profesion')
  if (normStr(row.direccion) !== normStr(db.direccion)) changes.push('direccion')
  if (normStr(row.municipio) !== normStr(db.municipio)) changes.push('municipio')
  if (normStr(row.departamento) !== normStr(db.departamento)) changes.push('departamento')
  if (normStr(row.telefono_e164) !== normStr(db.telefono_e164)) changes.push('telefono_e164')
  if (normStr(row.usuario_core) !== normStr(db.usuario_core)) changes.push('usuario_core')
  if (normStr(row.actividad_economica) !== normStr(db.actividad_economica)) changes.push('actividad_economica')
  return changes
}

function diffCuentaFields(row: ImportRow, db: CuentaDb): string[] {
  const changes: string[] = []
  if (row.tipo_servicio !== db.tipo_servicio) changes.push('tipo_servicio')
  if (normStr(row.pin_core) !== normStr(db.pin_core)) changes.push('pin_core')
  if (!dateEq(row.fecha_apertura, db.fecha_apertura)) changes.push('fecha_apertura')
  if (!dateEq(row.ultimo_movimiento, db.ultimo_movimiento)) changes.push('ultimo_movimiento')
  if (!dateEq(row.fecha_vencimiento, db.fecha_vencimiento)) changes.push('fecha_vencimiento')
  if (!dateEq(row.fecha_cierre, db.fecha_cierre)) changes.push('fecha_cierre')
  if (row.cierre_anticipado !== db.cierre_anticipado) changes.push('cierre_anticipado')
  if (!numEq(row.saldo_disponible, db.saldo_disponible)) changes.push('saldo_disponible')
  if (!numEq(row.saldo_pignorado, db.saldo_pignorado)) changes.push('saldo_pignorado')
  if (!numEq(row.saldo_total, db.saldo_total)) changes.push('saldo_total')
  if (row.estado !== db.estado) changes.push('estado')
  return changes
}

// -----------------------------------------------------------------------------
// Fetchers de DB
// -----------------------------------------------------------------------------

async function fetchAsociadosDb(): Promise<AsociadoDb[]> {
  const { data, error } = await supabase
    .from('asociado')
    .select(
      'id, no_asociado, dpi, nombre_completo, genero, profesion, direccion, municipio, departamento, telefono_e164, usuario_core, actividad_economica',
    )
    .limit(10000)
  if (error) throw error
  return (data ?? []) as AsociadoDb[]
}

async function fetchCuentasDb(): Promise<CuentaDb[]> {
  const { data, error } = await supabase
    .from('cuenta')
    .select(
      'cuenta_id, asociado_id, no_asociado, tipo_servicio, pin_core, fecha_apertura, ultimo_movimiento, fecha_vencimiento, fecha_cierre, cierre_anticipado, saldo_disponible, saldo_pignorado, saldo_total, estado',
    )
    .limit(20000)
  if (error) throw error
  return (data ?? []) as CuentaDb[]
}

async function fetchPreviousRun(fileHash: string): Promise<HistoricRun | null> {
  const { data, error } = await supabase
    .from('core_import_run')
    .select('id, archivo_nombre, archivo_hash, status, rows_total, started_at, finished_at')
    .eq('archivo_hash', fileHash)
    .order('started_at', { ascending: false })
    .limit(1)
  if (error) throw error
  return (data?.[0] as HistoricRun) ?? null
}

// -----------------------------------------------------------------------------
// Diff principal
// -----------------------------------------------------------------------------

export async function diffAgainstDb(parsed: ParseResult): Promise<DiffResult> {
  const [asociadosDb, cuentasDb, previousRun] = await Promise.all([
    fetchAsociadosDb(),
    fetchCuentasDb(),
    fetchPreviousRun(parsed.fileHash),
  ])

  const asociadosDbMap = new Map<string, AsociadoDb>(asociadosDb.map((a) => [a.no_asociado, a]))
  const asociadosDbByDpi = new Map<string, AsociadoDb>(asociadosDb.map((a) => [a.dpi, a]))
  const cuentasDbMap = new Map<string, CuentaDb>(cuentasDb.map((c) => [c.cuenta_id, c]))

  const warnings: DiffWarning[] = []

  // Colapsar filas del xlsx a: un record por no_asociado (asociado)
  // y un record por cuenta_id (cuenta). El xlsx tiene una fila por producto,
  // asi que muchas filas pueden referirse al mismo asociado.
  const asociadosXlsx = new Map<string, ImportRow>()
  const cuentasXlsx = new Map<string, ImportRow>()
  for (const row of parsed.rows) {
    if (!asociadosXlsx.has(row.no_asociado)) asociadosXlsx.set(row.no_asociado, row)
    cuentasXlsx.set(row.cuenta_id, row)
  }

  // Warning: re-import del mismo archivo
  if (previousRun && previousRun.status === 'ok') {
    warnings.push({
      severity: 'warn',
      message: `Este archivo (hash ${parsed.fileHash.slice(0, 12)}…) ya se importó el ${new Date(previousRun.started_at).toLocaleString('es-GT')}. Podés aplicarlo de nuevo — el upsert es idempotente.`,
    })
  } else if (previousRun && previousRun.status === 'partial') {
    warnings.push({
      severity: 'warn',
      message: `Hubo un intento previo de este mismo archivo que quedó en estado "partial". Revisá qué filas faltaron antes de reintentar.`,
    })
  }

  // Warning: DPI duplicado con otro no_asociado en DB
  for (const [no_asociado, row] of asociadosXlsx) {
    const byDpi = asociadosDbByDpi.get(row.dpi)
    if (byDpi && byDpi.no_asociado !== no_asociado) {
      warnings.push({
        severity: 'error',
        message: `DPI ${row.dpi.slice(0, 3)}*******${row.dpi.slice(-3)} del xlsx (asociado ${no_asociado}) ya existe en DB con otro número de asociado (${byDpi.no_asociado}). No se aplicará hasta resolver el conflicto.`,
      })
    }
  }

  // Diff asociados
  const asociados: AsociadoDiff[] = []
  for (const [no_asociado, row] of asociadosXlsx) {
    const db = asociadosDbMap.get(no_asociado) ?? null
    if (!db) {
      asociados.push({
        no_asociado,
        nombre_completo: row.nombre_completo,
        dpi: row.dpi,
        status: 'nuevo',
        changedFields: [],
        row,
        dbRow: null,
      })
    } else {
      const changes = diffAsociadoFields(row, db)
      asociados.push({
        no_asociado,
        nombre_completo: row.nombre_completo,
        dpi: row.dpi,
        status: changes.length === 0 ? 'sin_cambios' : 'modificado',
        changedFields: changes,
        row,
        dbRow: db,
      })
    }
  }

  // Diff cuentas
  const cuentas: CuentaDiff[] = []
  for (const [cuenta_id, row] of cuentasXlsx) {
    const db = cuentasDbMap.get(cuenta_id) ?? null
    if (!db) {
      cuentas.push({
        cuenta_id,
        no_asociado: row.no_asociado,
        tipo_servicio: row.tipo_servicio,
        status: 'nuevo',
        changedFields: [],
        row,
        dbRow: null,
      })
    } else {
      const changes = diffCuentaFields(row, db)
      cuentas.push({
        cuenta_id,
        no_asociado: row.no_asociado,
        tipo_servicio: row.tipo_servicio,
        status: changes.length === 0 ? 'sin_cambios' : 'modificado',
        changedFields: changes,
        row,
        dbRow: db,
      })
    }
  }

  const counts = {
    asociadosNuevos: asociados.filter((a) => a.status === 'nuevo').length,
    asociadosModificados: asociados.filter((a) => a.status === 'modificado').length,
    asociadosSinCambios: asociados.filter((a) => a.status === 'sin_cambios').length,
    cuentasNuevas: cuentas.filter((c) => c.status === 'nuevo').length,
    cuentasModificadas: cuentas.filter((c) => c.status === 'modificado').length,
    cuentasSinCambios: cuentas.filter((c) => c.status === 'sin_cambios').length,
  }

  return { asociados, cuentas, warnings, previousRunSameHash: previousRun, counts }
}
